import React, { useEffect, useState } from "react";

import {
  getPreferencesByLeadId,
  FetchedPreferenceItem,
  getAllPreferenceTypes,
  PreferenceItem,
} from "../Services/APIService";

interface ViewPreferenceProps {
  leadId: number;
  refreshTrigger?: number;
}

interface GroupedPreferences {
  [preferenceTypeName: string]: {
    values: string[];
    specialNotes: string[];
  };
}

const ViewPreferenceType: React.FC<ViewPreferenceProps> = ({
  leadId,
  refreshTrigger,
}) => {
  const [preferences, setPreferences] = useState<FetchedPreferenceItem[]>([]);
  const [preferenceTypes, setPreferenceTypes] = useState<PreferenceItem[]>([]);
  const [loading, setLoading] = useState<boolean>(false);

  const fetchPreferences = async () => {
    setLoading(true);
    try {
      console.log("lead", leadId);

      const [preferencesData, typesData] = await Promise.all([
        getPreferencesByLeadId(leadId),
        getAllPreferenceTypes(),
      ]);
      setPreferences(preferencesData);
      setPreferenceTypes(typesData);
    } catch (error) {
      console.error("Failed to fetch preferences", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPreferences();
  }, [leadId, refreshTrigger]);

  // Initialize tooltips
  useEffect(() => {
    // Create custom tooltip element if it doesn't exist
    if (!document.getElementById("custom-tooltip")) {
      const tooltipEl = document.createElement("div");
      tooltipEl.id = "custom-tooltip";
      tooltipEl.className = "custom-tooltip"; // Add class for styling
      tooltipEl.style.display = "none";
      document.body.appendChild(tooltipEl);
    }

    // Define event handlers as EventListener
    const handleMouseEnter: EventListener = (e: Event) => {
      const tooltip = document.getElementById("custom-tooltip");
      const tooltipTriggerEl = e.currentTarget as HTMLElement;
      const title = tooltipTriggerEl.getAttribute("data-tooltip-content") || "";

      if (!title) return;

      if (tooltip) {
        tooltip.textContent = title;
        tooltip.style.display = "block";

        // Position the tooltip to the right
        const rect = tooltipTriggerEl.getBoundingClientRect();
        const scrollX = window.scrollX || window.pageXOffset;
        const scrollY = window.scrollY || window.pageYOffset;

        setTimeout(() => {
          if (tooltip) {
            tooltip.style.left = `${rect.right + scrollX + 10}px`; // Right side + 10px offset
            tooltip.style.top = `${rect.top + scrollY}px`; // Align with top of element
            tooltip.style.transform = "translateY(-50%)"; // Center vertically
          }
        }, 0);
      }
    };

    const handleMouseLeave: EventListener = (e: Event) => {
      const tooltip = document.getElementById("custom-tooltip");
      if (tooltip) {
        tooltip.style.display = "none";
      }
    };

    // Initialize tooltip functionality
    const tooltipTriggerList = document.querySelectorAll("[data-tooltip]");
    tooltipTriggerList.forEach((tooltipTriggerEl) => {
      tooltipTriggerEl.addEventListener("mouseenter", handleMouseEnter);
      tooltipTriggerEl.addEventListener("mouseleave", handleMouseLeave);
    });

    // Cleanup function to remove event listeners
    return () => {
      tooltipTriggerList.forEach((el) => {
        el.removeEventListener("mouseenter", handleMouseEnter);
        el.removeEventListener("mouseleave", handleMouseLeave);
      });
    };
  }, [preferences]);

  // Group preferences by type
  const groupedPreferences: GroupedPreferences = preferences.reduce(
    (acc, pref) => {
      if (!acc[pref.preferenceTypeName]) {
        acc[pref.preferenceTypeName] = {
          values: [],
          specialNotes: [],
        };
      }

      acc[pref.preferenceTypeName].values.push(pref.preferenceValue);
      if (pref.specialNotes) {
        acc[pref.preferenceTypeName].specialNotes.push(pref.specialNotes);
      }

      return acc;
    },
    {} as GroupedPreferences
  );

  // Function to remove "Preference" from preference type names
  const cleanPreferenceName = (name: string) => {
    return name.replace(/\s*Preference\s*$/i, "").trim();
  };

  if (loading)
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ height: "100px" }}
      >
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );

  return (
    <div
    // className="container-fluid p-3"
    // style={{ maxHeight: "60vh", overflowY: "auto" }}
    >
      <div className="row g-4">
        {preferenceTypes.map((preferenceType) => {
          const hasPreference =
            groupedPreferences[preferenceType.preferenceName];
          const cleanName = cleanPreferenceName(preferenceType.preferenceName);

          return (
            <div key={preferenceType.id} className="col-md-6 col-lg-3">
              <div className="mb-2">
                <div
                  className="fw-semibold text-muted mb-1"
                  style={{ fontSize: "13px" }}
                >
                  {cleanName}
                </div>
                <div
                  className="text-dark"
                  style={{ fontSize: "0.785rem", lineHeight: "1.4" }}
                >
                  {hasPreference && hasPreference.values.length > 0 ? (
                    hasPreference.values.map((value, index) => (
                      <span
                        key={index}
                        data-tooltip
                        data-tooltip-content={
                          hasPreference.specialNotes[index] || ""
                        }
                        className="preference-value-hover"
                        style={{
                          cursor: hasPreference.specialNotes[index]
                            ? "pointer"
                            : "default",
                        }}
                      >
                        {value}
                        {index < hasPreference.values.length - 1 && ", "}
                      </span>
                    ))
                  ) : (
                    <span className="text-muted fst-italic">-</span>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ViewPreferenceType;
